import { useMemo } from "react"

import SuperChart, { type StackedBarChartData } from "~/components/reusable/graphs/SuperChart"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { ChartType, LegendAlignment } from "~/constants/graphs"
import type { InsightScalars } from "~/utils/demandInsightValues"
import { formatNumber } from "~/utils/formatting"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { formatName } from "~/utils/namingUtils"
import { getTailwindValue } from "~/utils/tailwind"

/** The comparables' stockout reading summed over one set of variants — the whole route, or one node. */
export type StockoutFigures = {
  /** What the comparables sold, as the baseline folds it. */
  comparableSales: number
  /** What they could not sell while out of stock, ≥ 0. */
  lostUnits: number
}

/** Σ of the engine's per-variant scalars: the row's own fold, so chart and switch agree. */
export const sumStockoutFigures = (
  variants: readonly IVariantSummary[],
  scalarsFor: (variantId: string) => InsightScalars | null,
): StockoutFigures => {
  const out: StockoutFigures = { comparableSales: 0, lostUnits: 0 }
  for (const v of variants) {
    const s = scalarsFor(v.variantId)
    if (!s) continue
    out.comparableSales += s.baseline ?? 0
    out.lostUnits += s.comparableStockoutUnits ?? 0
  }
  return out
}

/**
 * Where "Estimated demand during stockout" comes from: what the comparables sold plus what they
 * couldn't serve out of stock, summing to what adding lost sales plans on. One column, not a
 * weekly line: the insight is an overall figure.
 */
const StockoutsBreakdown = ({ figures }: { figures: StockoutFigures }) => {
  const { comparableSales, lostUnits } = figures
  // NOOS measures on exactly what the baseline reads (own sales or matches), not on comparables.
  const ownSales = useDemand2Scope().kind === "noos"
  const sales = formatName("sale", true, true)
  const data = useMemo(
    (): StackedBarChartData[] => [
      {
        name: sales,
        type: "column",
        color: getTailwindValue("--color-purpleheart-500"),
        data: [Math.round(comparableSales)],
      },
      {
        name: "Lost to stockouts",
        type: "column",
        color: getTailwindValue("--color-violet-300"),
        data: [Math.round(lostUnits)],
      },
    ],
    [comparableSales, lostUnits, sales],
  )

  if (comparableSales <= 0 && lostUnits <= 0) return null

  return (
    <div className="flex flex-col gap-3">
      <SuperChart
        type={ChartType.StackedColumn}
        data={data}
        xCategories={[ownSales ? "Baseline variants" : "Comparable variants"]}
        height={240}
        legendAlignment={LegendAlignment.Bottom}
      />
      <p className="max-w-[52ch] paragraph-sm text-fg-subtle">
        {`${ownSales ? "In the weeks the baseline reads, its sales" : "The comparable variants"} sold ${formatNumber(Math.round(comparableSales))} units and lost ${formatNumber(Math.round(lostUnits))} more while out of stock. Without stockouts they would have sold ${formatNumber(Math.round(comparableSales + lostUnits))}.`}
      </p>
    </div>
  )
}

export default StockoutsBreakdown
